import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { ConsentCategory } from '@packages/shared/src/utils/getCookieConsent/getCookieConsent';

import { hasFeatureFlag } from '../utils/featureFlags/hasFeatureFlag';
import { hideGoogleAdsBannerInProductLists } from '../activeFeatureFlags';

type AdSlotType = 'skyscraper' | 'banner';
type AdMode = 'containerOnly' | 'doubleClick';

/**
 * Hook to determine if an ad slot should be visible
 *
 * @param slotType - Type of ad slot ('skyscraper' or 'banner')
 * @param majorMkz - Major product group (used for blacklist check)
 * @param adMode - Optional filter for specific ad server mode
 * @returns true if the ad slot should be visible, false otherwise
 */
export const useShouldAdSlotBeVisible = (
  slotType: AdSlotType,
  majorMkz?: number,
  adMode?: AdMode,
): boolean => {
  const { getCookies } = useCookies();
  const {
    search: { adServer },
  } = useConfig();

  const hasMarketingConsent = useCookieConsent(ConsentCategory.Marketing);

  const { mode, mkzBlacklist } = adServer;

  // Early returns for common blocking conditions
  if (mode === 'disabled') return false;
  if (!hasMarketingConsent) return false;
  if (majorMkz !== undefined && mkzBlacklist.includes(majorMkz)) return false;
  if (adMode && mode !== adMode) return false;

  // Slot-specific visibility rules
  if (slotType === 'skyscraper') return true; // Skyscraper viewport visibility is handled via CSS (hidden lg:block) in the consumer component (SearchAdServerIntegration.skyscraper.layout.tsx)

  if (slotType === 'banner') {
    // Negative test: show banner when feature flag is NOT active
    return !hasFeatureFlag(hideGoogleAdsBannerInProductLists, getCookies());
  }

  return false;
};
